import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslations } from "../hooks/useTranslations";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const translations = useTranslations();

  // Check scroll position
  const checkScroll = () => {
    if (window.scrollY > 20) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  };

  // Run once on component mount and when location changes
  useEffect(() => {
    checkScroll();
    const handleScroll = () => {
      checkScroll();
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]); // Re-run when route changes

  // Close menu when navigating
  useEffect(() => {
    setIsMenuOpen(false);
    setIsServicesDropdownOpen(false);
  }, [location.pathname]);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  // Helper function to preserve language parameter when navigating
  const getUrlWithLang = (path: string) => {
    const urlParams = new URLSearchParams(location.search);
    const langParam = urlParams.get("lang");

    if (langParam && ["en", "fr"].includes(langParam)) {
      // If path has a hash fragment, handle it properly
      if (path.includes("#")) {
        const [basePath, hash] = path.split("#");
        return `${basePath}?lang=${langParam}#${hash}`;
      }
      return `${path}?lang=${langParam}`;
    }

    return path;
  };

  // Service pages data
  const servicePages = [
    {
      name: translations.t("services.detailed.services.treePruning.title"),
      path: "/services/tree-pruning",
    },
    {
      name: translations.t("services.detailed.services.gardening.title"),
      path: "/services/gardening",
    },
    {
      name: translations.t("services.detailed.services.grassCutting.title"),
      path: "/services/grasscutting",
    },
    {
      name: translations.t(
        "services.detailed.services.springFallCleanup.title"
      ),
      path: "/services/spring-fall-cleanup",
    },
    {
      name: translations.t(
        "services.detailed.services.parkingLotCleaning.title"
      ),
      path: "/services/parking-lot-cleaning",
    },
    {
      name: translations.t("services.detailed.services.hedgeTrimming.title"),
      path: "/services/hedge-trimming",
    },
    {
      name: translations.t("services.detailed.services.sodInstallation.title"),
      path: "/services/sod-installation",
    },
    {
      name: translations.t("services.detailed.services.relevelling.title"),
      path: "/services/relevelling",
    },
    {
      name: translations.t("services.detailed.services.paveWalkways.title"),
      path: "/services/pave-walkways",
    },
    {
      name: translations.t("services.detailed.services.paveUniReleveling.title"),
      path: "/services/pave-uni-releveling",
    },
    {
      name: translations.t("services.detailed.services.excavation.title"),
      path: "/services/excavation",
    },
    {
      name: translations.t("services.detailed.services.snowRemoval.title"),
      path: "/services/snow-removal",
    },
  ];

  return (
    <>
      {/* Navbar */}
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          isMenuOpen || scrolled ? "bg-primary-900 shadow-lg" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Link
                  to={getUrlWithLang("/")}
                  className="text-white flex items-center"
                >
                  <img
                    src="/bearz_logo.png"
                    alt="Bearz Landscaping Logo"
                    className="h-10 sm:h-12 md:h-16 w-auto"
                  />
                </Link>
              </div>
            </div>

            {/* Desktop menu */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-center space-x-4 lg:space-x-6">
                <Link
                  to={getUrlWithLang("/")}
                  className={`${
                    location.pathname === "/"
                      ? "text-primary-400 font-semibold"
                      : "text-white"
                  } hover:text-primary-400 px-2 lg:px-3 py-2 text-sm lg:text-md transition-colors duration-300 relative group`}
                >
                  <span>{translations.navbar.home}</span>
                  <span
                    className={`absolute bottom-0 left-0 w-full h-0.5 bg-primary-400 transform origin-left scale-x-0 transition-transform duration-300 ${
                      location.pathname === "/"
                        ? "scale-x-100"
                        : "group-hover:scale-x-100"
                    }`}
                  ></span>
                </Link>

                {/* Services Dropdown */}
                <div
                  className="relative"
                  onMouseEnter={() => setIsServicesDropdownOpen(true)}
                  onMouseLeave={() => setIsServicesDropdownOpen(false)}
                >
                  <Link
                    to={getUrlWithLang("/services")}
                    className={`${
                      location.pathname.startsWith("/services")
                        ? "text-primary-400 font-semibold"
                        : "text-white"
                    } hover:text-primary-400 px-2 lg:px-3 py-2 text-sm lg:text-md transition-colors duration-300 relative group flex items-center`}
                  >
                    <span>{translations.navbar.services}</span>
                    <svg
                      className="ml-1 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    <span
                      className={`absolute bottom-0 left-0 w-full h-0.5 bg-primary-400 transform origin-left scale-x-0 transition-transform duration-300 ${
                        location.pathname.startsWith("/services")
                          ? "scale-x-100"
                          : "group-hover:scale-x-100"
                      }`}
                    ></span>
                  </Link>

                  {/* Dropdown Menu */}
                  <div
                    className={`absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-neutral-200 transition-all duration-300 ${
                      isServicesDropdownOpen
                        ? "opacity-100 visible transform translate-y-0"
                        : "opacity-0 invisible transform -translate-y-2"
                    }`}
                  >
                    <div className="py-2">
                      <Link
                        to={getUrlWithLang("/services")}
                        className="block px-4 py-2 text-sm text-neutral-700 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200 font-medium border-b border-neutral-100"
                      >
                        {translations.t("services.detailed.title")}
                      </Link>
                      {servicePages.map((service, index) => (
                        <Link
                          key={index}
                          to={getUrlWithLang(service.path)}
                          className="block px-4 py-2 text-sm text-neutral-700 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>

                <Link
                  to={getUrlWithLang(
                    location.pathname === "/gallery"
                      ? "/gallery"
                      : "/#gallery-section"
                  )}
                  className={`${
                    location.pathname === "/gallery"
                      ? "text-primary-400 font-semibold"
                      : "text-white"
                  } hover:text-primary-400 px-2 lg:px-3 py-2 text-sm lg:text-md transition-colors duration-300 relative group`}
                >
                  <span>{translations.navbar.gallery}</span>
                  <span
                    className={`absolute bottom-0 left-0 w-full h-0.5 bg-primary-400 transform origin-left scale-x-0 transition-transform duration-300 ${
                      location.pathname === "/gallery"
                        ? "scale-x-100"
                        : "group-hover:scale-x-100"
                    }`}
                  ></span>
                </Link>

                <Link
                  to={getUrlWithLang(
                    location.pathname === "/about"
                      ? "/about"
                      : "/#about-section"
                  )}
                  className={`${
                    location.pathname === "/about"
                      ? "text-primary-400 font-semibold"
                      : "text-white"
                  } hover:text-primary-400 px-2 lg:px-3 py-2 text-sm lg:text-md transition-colors duration-300 relative group`}
                >
                  <span>{translations.navbar.about}</span>
                  <span
                    className={`absolute bottom-0 left-0 w-full h-0.5 bg-primary-400 transform origin-left scale-x-0 transition-transform duration-300 ${
                      location.pathname === "/about"
                        ? "scale-x-100"
                        : "group-hover:scale-x-100"
                    }`}
                  ></span>
                </Link>

                <Link
                  to={getUrlWithLang("/contact")}
                  className="bg-primary-500 hover:bg-primary-600 text-white font-medium px-4 lg:px-5 py-2 lg:py-2.5 rounded-full shadow-md hover:shadow-lg transition-all duration-300 flex items-center text-sm lg:text-md"
                >
                  <span>{translations.navbar.contact}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    className="w-3.5 h-3.5 lg:w-4 lg:h-4 ml-1.5 lg:ml-2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </Link>

                {/* Language Switcher */}
                <LanguageSwitcher />
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-white hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                aria-expanded={isMenuOpen}
              >
                <span className="sr-only">
                  {isMenuOpen ? "Close main menu" : "Open main menu"}
                </span>
                {isMenuOpen ? (
                  <svg
                    className="block h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    className="block h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu - completely separate from navbar */}
      {isMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-primary-900 z-40"
          style={{ top: "0", left: "0", right: "0", bottom: "0" }}
        >
          <div className="flex justify-end p-4">
            <button
              onClick={() => setIsMenuOpen(false)}
              className="text-white hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 p-2"
            >
              <span className="sr-only">Close menu</span>
              <svg
                className="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="px-4 pt-2 pb-6 space-y-3 flex flex-col items-center overflow-y-auto max-h-screen">
            <Link
              to={getUrlWithLang("/")}
              className={`${
                location.pathname === "/" ? "text-primary-400" : "text-white"
              } w-full text-center py-4 text-lg font-medium`}
              onClick={() => setIsMenuOpen(false)}
            >
              {translations.navbar.home}
            </Link>

            {/* Services with submenu in mobile */}
            <div className="w-full">
              <button
                onClick={() =>
                  setIsServicesDropdownOpen(!isServicesDropdownOpen)
                }
                className={`${
                  location.pathname.startsWith("/services")
                    ? "text-primary-400"
                    : "text-white"
                } w-full text-center py-4 text-lg font-medium flex items-center justify-center`}
              >
                {translations.navbar.services}
                <svg
                  className={`ml-2 h-4 w-4 transition-transform duration-200 ${
                    isServicesDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {isServicesDropdownOpen && (
                <div className="bg-primary-800 rounded-lg mx-4 mb-2">
                  <Link
                    to={getUrlWithLang("/services")}
                    className="block px-4 py-3 text-white hover:text-primary-200 transition-colors duration-200 text-center font-medium border-b border-primary-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {translations.t("services.detailed.title")}
                  </Link>
                  {servicePages.map((service, index) => (
                    <Link
                      key={index}
                      to={getUrlWithLang(service.path)}
                      className="block px-4 py-3 text-white hover:text-primary-200 transition-colors duration-200 text-center"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to={getUrlWithLang(
                location.pathname === "/gallery"
                  ? "/gallery"
                  : "/#gallery-section"
              )}
              className={`${
                location.pathname === "/gallery"
                  ? "text-primary-400"
                  : "text-white"
              } w-full text-center py-4 text-lg font-medium`}
              onClick={() => setIsMenuOpen(false)}
            >
              {translations.navbar.gallery}
            </Link>
            <Link
              to={getUrlWithLang(
                location.pathname === "/about" ? "/about" : "/#about-section"
              )}
              className={`${
                location.pathname === "/about"
                  ? "text-primary-400"
                  : "text-white"
              } w-full text-center py-4 text-lg font-medium`}
              onClick={() => setIsMenuOpen(false)}
            >
              {translations.navbar.about}
            </Link>
            <div className="w-full pt-4">
              <Link
                to={getUrlWithLang("/contact")}
                className="bg-primary-500 hover:bg-primary-600 text-white font-medium px-8 py-3 rounded-full shadow-lg block text-center mx-auto max-w-xs text-lg"
                onClick={() => setIsMenuOpen(false)}
              >
                {translations.navbar.contact}
              </Link>
            </div>

            {/* Language Switcher for mobile */}
            <div className="w-full pt-4 flex justify-center">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
